import { useMemo, useState, type CSSProperties } from 'react'

import { Card, DataTable, Notice, Select, type Column } from '../ui'
import type { RecordingRecord } from '../../lib/api'
import type { DailyStatusResult, ScoreSlot } from '../../lib/dailyStatus'
import { deviceLabel } from '../../lib/devices'
import { formatScore, formatSessionDate } from '../../lib/format'
import { CONDITION_WORDS } from '../../lib/recordings'
import {
  DEFAULT_TREND_SLOT,
  TREND_GEOMETRY,
  buildTrend,
  linePoints,
  markerFor,
  markerPath,
  plotX,
  plotY,
  trendOptions,
  type Trend,
  type TrendPoint,
  type TrendSeries,
} from '../../lib/trend'
import type { FanOutEntry } from '../../lib/useFanOut'

/**
 * The trend — §4.3 and §4.4, and the screen the spec calls "the one most likely
 * to overclaim".
 *
 * ── ONE CARD PER CONDITION ──────────────────────────────────────────────────
 *
 * §4.3's first hard rule is "never connect across a condition boundary. EC and
 * EO are separate series and separate baselines; a line joining them asserts
 * something false." A single chart with two series makes that a rule somebody
 * has to keep. Two cards make it unrepresentable: there is no element that could
 * hold both conditions, and `linePoints` takes a `TrendSeries` rather than a
 * list of points, so no argument exists that could describe the wrong line.
 *
 * The x domain is still shared, so one calendar date sits at the same position
 * in both cards and they can be read against each other left to right — which is
 * the only comparison between the two conditions that is actually true.
 *
 * ── AND ONE CARD PER HEADBAND (Sprint 1.9.2 S4) ─────────────────────────────
 *
 * Each device keeps its own baseline, so a line from a BrainBit Pro reading to
 * a four-contact BrainBit reading joins two numbers measured against different
 * references. A series is one condition on one headband (`buildTrend`), so the
 * same construction that keeps EC and EO apart keeps the headbands apart. When
 * the account has recorded with more than one headband every card, the hint,
 * the summary and the table name it; with one headband nothing here changes.
 *
 * ── THIS COMPONENT MAKES NO REQUESTS ────────────────────────────────────────
 *
 * It is handed what History already read. That is how §4.5's "keep chart
 * requests bounded as pages load" is satisfied: not by a limit inside the chart
 * but by the chart having nothing to request.
 */

export interface TrendChartProps {
  rows: readonly RecordingRecord[]
  /** Keyed by `recording_id`. Every non-`ready` state is SAID, not hidden. */
  entries: Readonly<Record<string, FanOutEntry<DailyStatusResult> | undefined>>
  /** `usePagedList.isComplete`. False means `rows` is a prefix of the history. */
  isComplete: boolean
}

export function TrendChart({ rows, entries, isComplete }: TrendChartProps) {
  const [slot, setSlot] = useState<ScoreSlot>(DEFAULT_TREND_SLOT)
  const options = useMemo(() => trendOptions(entries), [entries])
  const trend = useMemo(() => buildTrend(rows, entries, slot), [rows, entries, slot])
  const title = options.find((o) => o.slot === slot)?.title ?? null

  // Nothing readable has arrived, so there is no measure to name and no scale to
  // draw. Saying so beats an empty pair of axes, which reads as "you have no
  // recordings" rather than as "this has not loaded".
  if (options.length === 0) {
    return (
      <Card as="section" className="trend" aria-labelledby="trend-heading">
        <div className="card__head">
          <TrendHeading />
        </div>
        <p className="trend__note" role="status">
          {trend.coverage.loading > 0
            ? 'Reading your results…'
            : 'No results loaded yet, so there is nothing to chart.'}
        </p>
      </Card>
    )
  }

  return (
    <Card as="section" className="trend" aria-labelledby="trend-heading">
      <div className="card__head">
        <TrendHeading />
        {/* The charted-range sentence is the card's meta line. `.card__meta` is
            the design's voice for it; `.trend__range` is the class the tests and
            the live-region inventory key on, and it stays on this element. */}
        <p className="card__meta trend__range">{rangeSentence(trend, isComplete, title)}</p>
      </div>

      <Select
        label="Measure"
        value={slot}
        onChange={setSlot}
        options={options.map((o) => ({ value: o.slot, label: o.title }))}
        hint={
          trend.byDevice
            ? 'Each condition and each headband is charted on its own, because they are measured against separate baselines.'
            : 'Each condition is charted on its own, because they are measured against separate baselines.'
        }
      />

      {trend.series.length === 0 ? (
        <Notice tone="info">
          No {title ?? 'values'} in the recordings loaded so far.
        </Notice>
      ) : (
        <div className="trend__pair">
          {trend.series.map((series) => (
            <ConditionChart
              key={`${series.deviceId}/${series.condition}`}
              series={series}
              title={title}
              trend={trend}
              baselineAt={trend.baselineAt}
            />
          ))}
        </div>
      )}

      <Coverage trend={trend} title={title} />

      <details className="trend__values">
        <summary>All charted values</summary>
        <ValueTable trend={trend} />
      </details>
    </Card>
  )
}

/**
 * History's section heading, WEARING the card's eyebrow voice.
 *
 * It stays an `<h2 id="trend-heading">` — the document's second-level heading
 * and the section's accessible name — and only borrows the eyebrow's
 * typography. `Card`'s own `eyebrow` prop renders a `<div>`, which would take
 * the heading out of the outline, so the head is written here instead.
 *
 * `card__eyebrow--muted` is explicit rather than defaulted: a hand-written
 * caller names the tone it wants instead of inheriting whichever default the
 * primitive happens to carry (R25).
 */
function TrendHeading() {
  return (
    <h2 id="trend-heading" className="card__eyebrow card__eyebrow--muted trend__heading">
      Trend
    </h2>
  )
}

/**
 * One condition's card.
 *
 * ── NO `<text>` INSIDE THE SVG ──────────────────────────────────────────────
 *
 * Every label — the 0 and 100, the two dates, the legend — is HTML beside the
 * drawing rather than inside it. Three things follow: the typography tokens
 * apply, the text does not scale with the `viewBox` (so it stays readable at
 * 360px and does not become enormous on a wide screen), and every label is
 * assertable with `getByText` rather than being invisible to a runner with no
 * layout engine.
 *
 * The SVG is `role="img"` with a sentence for its name and contains **no
 * focusable element**: a focusable descendant of a presentational subtree is an
 * axe violation, and the keyboard path to every value is the table below, which
 * is a real table rather than a roving-tabindex re-implementation.
 */
function ConditionChart({
  series,
  title,
  trend,
  baselineAt,
}: {
  series: TrendSeries
  title: string | null
  trend: Trend
  /** The PAYLOAD's mark on this slot's scale, or null/absent. Never a constant. */
  baselineAt?: number | null
}) {
  const g = TREND_GEOMETRY
  const words = CONDITION_WORDS[series.condition]
  const single = series.points.length === 1
  // Named only when the account has more than one headband; see `Trend.byDevice`.
  const device = trend.byDevice ? headbandWords(series.deviceId) : null

  return (
    /*
      A FLAT card inside the Trend card (v3's `.trend__pair`). The head is
      written here rather than passed as `Card`'s `eyebrow`, because `Card` only
      opens a `.card__head` when it is also given a `meta` or a `head` — and the
      design's condition card has a head with nothing in it but the eyebrow.
      The tone is named rather than defaulted, for the reason R25 gives.
    */
    <Card flat>
      <div className="card__head">
        <div className="card__eyebrow card__eyebrow--muted">
          {`${words} · ${device === null ? '' : `${device} · `}${series.points.length} ${series.points.length === 1 ? 'recording' : 'recordings'}`}
        </div>
      </div>
      <div className="trend__plot">
        {/* THREE LABELS WHEN THE PAYLOAD NAMES A MIDPOINT, TWO OTHERWISE.
            R20 fixed this at two because "a rule drawn across the middle is
            indistinguishable from a baseline reference, and the client has no
            baseline to reference". `display[slot].scale_baseline` is that
            reference, so the premise is spent -- the same way R19's first one
            was -- and the middle label is the payload's number, never a
            constant. With no mark there is still nothing to name, and the
            gutter falls back to the two ends it always had. */}
        <div className="trend__gutter" aria-hidden="true">
          <span>100</span>
          {typeof baselineAt === 'number' && <span className="trend__gutter-mark">{baselineAt}</span>}
          <span>0</span>
        </div>
        {/* The drawing's own box. It is the `.trend__axis` row's column too, so
            the first date hangs under the first mark rather than under the
            gutter's "100"; and it is `position: relative`, which is the
            containing block a payload-driven scale mark would be placed in. */}
        <div className="trend__canvas">
          {/*
            ── THE ONLY MARK ON THE SCALE THE PAYLOAD MAY ASK FOR (O2) ──────

            There is no constant here, no tick and no word. When the payload
            names a point on this slot's scale the chart draws a hairline at it;
            when it does not — which is every payload that exists today — there
            is no element at all.

            It is a `<span>` OUTSIDE the svg and positioned by a custom property
            the stylesheet projects, because R14/O2 forbid JS arithmetic on a
            payload number: the client must not compute where a score sits. It
            is also unnamed, and stays unnamed until B1 carries a
            `scale_baseline_label` — any word the client chose for it would be a
            client-authored scientific label, and `TrendChart.test.tsx` forbids
            "baseline" in this body for exactly that reason.
          */}
          {typeof baselineAt === 'number' && (
            <span
              className="trend__baseline"
              aria-hidden="true"
              style={{ '--trend-baseline-at': baselineAt } as CSSProperties}
            />
          )}
          {/*
            ── THE MARKS LEAVE THE STRETCHED COORDINATE SYSTEM ──────────────

            This element had `viewBox="0 0 640 200"` and `preserveAspectRatio="none"`
            against a CSS box of `width:100%; height:160px`. That makes the scale
            ANISOTROPIC and by a factor nobody chose: sy is a literal constant of
            the cascade (160/200 = 0.8 below 768px, 1.0 above) and sx is
            renderedWidth/640 -- about 0.41 at 360px and 1.44 at 1280px. A marker
            occupies an exactly square 2r x 2r box (`markerPath`), so it drew as
            6.6 x 12.8 CSS px on a phone and a stretched ellipse on a desktop.
            Not a phone defect: the same defect, inverted.

            It matters because SHAPE is the channel that carries series identity
            (`markerFor`), which is what §7.2's non-colour encoding and criterion
            13 rest on -- and `markerFor`'s own docstring rests on "circle against
            diamond is the pair that stays distinguishable at 8px", a premise 6.6px
            falsifies.

            The escape has to be ABOVE the distortion, and that was measured
            rather than argued: a nested `<svg>` with its own `preserveAspectRatio`
            inside a distorting parent reports an IDENTICAL `getScreenCTM` and
            renders just as stretched, because its viewport is measured in the
            parent's already-scaled user space. So the outer element loses its
            viewBox entirely -- one user unit is one CSS pixel -- and the stretch
            is pushed down into the only thing that wants it.

            Nothing is measured and no `ResizeObserver` is involved: the marks are
            placed by PERCENTAGE, which the browser resolves against this
            viewport. Deleting one attribute does what a measuring hook would have
            done, which is why it was preferred to one.

            Why the sizing intent survives: `product.css` still fixes the height,
            so a wide screen still does not stretch a four-point chart to 400px
            tall. That comment there is unchanged and still true.
          */}
          <svg
            className={`trend__svg trend__svg--${series.condition.toLowerCase()}`}
            role="img"
            aria-label={summarise(series, title, trend)}
          >
            {/* One polyline per card, and a card holds one condition. Not drawn at
                all for a lone point: a line needs two ends, and a zero-length one
                is a dot pretending to be a trend.

                THE LINE KEEPS THE STRETCH, deliberately: a line's shape carries no
                meaning that distortion can damage, and stretching it is how the x
                axis fills the width. `vectorEffect` moved here from the marks,
                where at an identity CTM it had become a no-op -- on a
                non-uniformly scaled path it is the thing that stops a 1.5px pen
                drawing thicker on one axis than the other. */}
            {!single && (
              <svg
                className="trend__line-viewport"
                viewBox={`0 0 ${g.width} ${g.height}`}
                preserveAspectRatio="none"
                width="100%"
                height="100%"
              >
                <polyline
                  className="trend__line"
                  points={linePoints(series)}
                  fill="none"
                  vectorEffect="non-scaling-stroke"
                />
              </svg>
            )}
            {series.points.map((point) => (
              <Mark key={point.recordingId} point={point} title={title} />
            ))}
          </svg>
        </div>

        <div className="trend__axis" aria-hidden="true">
          <span>{trend.domain === null ? '' : formatSessionDate(trend.domain.firstDate)}</span>
          <span>{trend.domain === null ? '' : formatSessionDate(trend.domain.lastDate)}</span>
        </div>
      </div>

      {single && (
        <p className="trend__note">
          {device === null
            ? 'One recording in this condition. There is no trend to draw yet.'
            : 'One recording in this condition on this headband. There is no trend to draw yet.'}
        </p>
      )}
    </Card>
  )
}

function Mark({ point, title }: { point: TrendPoint; title: string | null }) {
  const marker = markerFor(point.condition, point.lowConfidence)
  const g = TREND_GEOMETRY
  /**
   * A NESTED VIEWPORT PER MARK, placed by percentage.
   *
   * `plotX`/`plotY` still decide where the point goes; dividing by the geometry
   * turns their answer into a fraction of the drawing, which the browser
   * resolves against the outer viewport. Nothing is measured, and the arithmetic
   * that positions the mark is the same arithmetic that positions the line, so
   * the two cannot drift apart.
   *
   * Inside this viewport one user unit is one CSS pixel -- the outer `<svg>` has
   * no viewBox -- so the mark is drawn around the origin at its true radius and
   * comes out 2r CSS px across at every viewport width.
   *
   * `overflow: visible` in the stylesheet is LOAD-BEARING, not decoration: the
   * UA rule `svg:not(:root) { overflow: hidden }` would clip this 1x1 viewport
   * and leave a speck. `trend.spec.ts` hit-tests the mark's centre for exactly
   * that reason -- a bounding box survives clipping and would not notice.
   */
  return (
    <svg
      className="trend__mark-viewport"
      x={`${(plotX(point.x) / g.width) * 100}%`}
      y={`${(plotY(point.y) / g.height) * 100}%`}
      width="1"
      height="1"
    >
      <path
        className={[
          'trend__mark',
          `trend__mark--${marker.shape}`,
          marker.fill === 'hollow' ? 'trend__mark--hollow' : 'trend__mark--solid',
        ].join(' ')}
        d={markerPath(marker.shape, 0, 0, marker.radius)}
        fill={marker.fill === 'hollow' ? 'none' : 'currentColor'}
        strokeDasharray={marker.dashed ? '3 2' : undefined}
      >
        {/* A native tooltip for a pointer, and nothing that only a pointer can
            reach: every value here is also in the table below. */}
        <title>
          {`${formatSessionDate(point.sessionDate)} — ${title ?? 'value'} ${formatScore(point.value)}`}
          {point.lowConfidence ? ', low confidence' : ''}
        </title>
      </path>
    </svg>
  )
}

/**
 * The legend, the coverage, and the one sentence criterion 3 needs on screen.
 *
 * A chart that silently omitted a recording would be a chart claiming the
 * person did not record that day. Every bucket that is not empty gets a
 * sentence, including the one that says a missing value is not a zero.
 */
function Coverage({ trend, title }: { trend: Trend; title: string | null }) {
  const baselineLabel = trend.baselineLabel
  const c = trend.coverage
  const measure = title ?? 'this measure'
  const lines: { key: string; text: string }[] = []
  if (c.loading > 0) {
    lines.push({ key: 'loading', text: `${c.loading} more ${plural(c.loading, 'recording')} still loading.` })
  }
  if (c.failed > 0) {
    lines.push({ key: 'failed', text: `${c.failed} ${plural(c.failed, 'result')} could not be loaded. Retry in the list below.` })
  }
  if (c.noValue > 0) {
    // Criterion 3, in words, on the screen. An absent value is not a low score.
    lines.push({
      key: 'noValue',
      text: `${c.noValue} ${plural(c.noValue, 'recording')} ${c.noValue === 1 ? 'has' : 'have'} no ${measure} value. That is not a score of zero.`,
    })
  }
  if (c.unreadable > 0) {
    lines.push({ key: 'unreadable', text: `${c.unreadable} ${plural(c.unreadable, 'result')} could not be read.` })
  }
  if (c.notScored > 0) {
    // NOT an error, and no reason hunted for: §2.6, criterion 8.
    lines.push({ key: 'notScored', text: `${c.notScored} ${plural(c.notScored, 'recording')} produced no result to chart.` })
  }

  return (
    <>
      <ul className="trend__legend">
        <li>
          <Glyph condition="EC" low={false} /> Eyes closed
        </li>
        <li>
          <Glyph condition="EO" low={false} /> Eyes open
        </li>
        <li>
          <Glyph condition="EC" low /> Hollow and dashed: held low confidence
        </li>
        {/* The fourth entry, and the only one whose words are not this file's.
            It renders only when every plotted payload agrees on a mark, and it
            names it with `scale_baseline_label` -- the client may not say what
            the position means. Drawn as a dashed rule rather than a `Glyph`, so
            it adds no `.trend__mark` to the unscoped counts the geometry tests
            take. OUTSIDE `.trend__canvas` on purpose: two assertions there
            forbid the word "baseline" inside the drawing itself. */}
        {baselineLabel !== null && (
          <li>
            <i className="trend__legend-rule" aria-hidden="true" /> {baselineLabel}
          </li>
        )}
      </ul>
      {/*
        ── THE LIVE REGION IS NOT ONE OF THE LIST ITEMS ──────────────────────
        `role="status"` sat on the `<li>` that reports outstanding reads, which
        axe fails as `list` (wcag2a, 1.3.1): an explicit role on a direct child
        of a `<ul>` stops it being a list item at all, and the 4.13 evaluator
        dropped the old "at least one real listitem" escape. Reachable on any
        history with more than four eligible rows, because `useFanOut` drains
        four at a time and every other key sits at `pending`.

        A region that is only in the DOM while it has something to say also
        announces unreliably — inserting it already populated is not a change.
        So it is always mounted and empty when idle, and the list is a list.

        ── AND THAT CLAIM IS TRUE OF THE TRANSITIONS, NOT OF THE FIRST MOUNT ──
        Corrected in Stage 7c. This region is mounted with the chart, and on a
        FIRST load the chart and `c.loading > 0` arrive together — so its first
        content is present at its first mount, which is the shape this comment
        warns about, one level up. Every later change announces properly, which
        is what it is for. The same is true of every loading state in the app
        and is why they were inventoried rather than swept: see
        `src/test/liveRegions.test.ts`, which also records that no test in this
        repo can observe an announcement at all.
      */}
      <p className="visually-hidden" role="status">
        {c.loading > 0 ? `${c.loading} more ${plural(c.loading, 'recording')} still loading.` : ''}
      </p>
      {lines.length > 0 && (
        <ul className="trend__coverage">
          {lines.map((line) => (
            <li key={line.key}>{line.text}</li>
          ))}
        </ul>
      )}
    </>
  )
}

/** Built by the same function the plot uses, so the key cannot drift from it. */
function Glyph({ condition, low }: { condition: 'EC' | 'EO'; low: boolean }) {
  const marker = markerFor(condition, low)
  const size = 18
  return (
    <svg
      className={`trend__glyph trend__svg--${condition.toLowerCase()}`}
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      aria-hidden="true"
    >
      <path
        className={`trend__mark trend__mark--${marker.fill === 'hollow' ? 'hollow' : 'solid'}`}
        d={markerPath(marker.shape, size / 2, size / 2, 6)}
        fill={marker.fill === 'hollow' ? 'none' : 'currentColor'}
        strokeDasharray={marker.dashed ? '3 2' : undefined}
      />
    </svg>
  )
}

/**
 * The readable alternative §4.3 asks for, and the only place an exact number
 * appears. `DataTable` rather than a second list implementation: it is a real
 * `<table>` above 768px and cards below, so it is readable in both renderings.
 */
function ValueTable({ trend }: { trend: Trend }) {
  const columns: Column<TrendPoint>[] = [
    { key: 'date', header: 'Date', render: (p) => formatSessionDate(p.sessionDate) },
    { key: 'condition', header: 'Condition', render: (p) => CONDITION_WORDS[p.condition] },
    // Only when there is more than one headband to tell apart: with one, the
    // table is exactly the one it always was.
    ...(trend.byDevice
      ? [{ key: 'headband', header: 'Headband', render: (p: TrendPoint) => headbandWords(p.deviceId) }]
      : []),
    {
      key: 'value',
      header: 'Value',
      align: 'end',
      // The only place a score becomes characters, here as everywhere.
      render: (p) => <span className="mono-cell">{formatScore(p.value)}</span>,
    },
    { key: 'label', header: 'Reading', render: (p) => p.label ?? '—' },
    {
      key: 'confidence',
      header: 'Confidence',
      align: 'end',
      render: (p) => (
        <span className="mono-cell">
          {p.confidence === null ? 'not available' : p.confidence}
          {p.lowConfidence ? ' · held low' : ''}
        </span>
      ),
    },
    {
      key: 'gap',
      header: 'Since the last',
      align: 'end',
      render: (p) =>
        p.daysFromPrevious === null
          ? trend.byDevice
            ? 'first in this condition on this headband'
            : 'first in this condition'
          : `${p.daysFromPrevious} ${plural(p.daysFromPrevious, 'day')}`,
    },
  ]
  return (
    <DataTable
      caption="Every charted recording, with its value"
      columns={columns}
      rows={[...trend.points]}
      rowKey={(p) => p.recordingId}
      empty={<p>Nothing charted yet.</p>}
    />
  )
}

const plural = (n: number, word: string) => (n === 1 ? word : `${word}s`)

/**
 * A headband's name and contact count, from the one device mapping. An id this
 * build cannot name is shown as the id itself, never as a guessed name.
 */
const headbandWords = (deviceId: string) => deviceLabel(deviceId) ?? deviceId

/**
 * One sentence, and it says the marks are joined only within a condition —
 * and, for an account with more than one headband, only within a headband.
 */
function summarise(series: TrendSeries, title: string | null, trend: Trend): string {
  const words = CONDITION_WORDS[series.condition].toLowerCase()
  const span =
    trend.domain === null
      ? ''
      : ` between ${formatSessionDate(trend.domain.firstDate)} and ${formatSessionDate(trend.domain.lastDate)}`
  const from = trend.byDevice ? ` from the ${headbandWords(series.deviceId)}` : ''
  const joined = trend.byDevice
    ? 'Only recordings in this condition from this headband are joined.'
    : 'Only recordings in this condition are joined.'
  return (
    `${title ?? 'Value'} for ${words} recordings${from}: ${series.points.length} ` +
    `${plural(series.points.length, 'point')} on a 0 to 100 scale${span}. ` +
    `${joined} The values are listed below the chart.`
  )
}

/**
 * §4.5, applied to the chart. "All" and "whole" appear in exactly one branch,
 * the same discipline `historyRange` follows for the list.
 */
function rangeSentence(trend: Trend, isComplete: boolean, title: string | null): string {
  const measure = title ?? 'this measure'
  if (trend.domain === null) {
    return isComplete
      ? `No ${measure} values in your history.`
      : `No ${measure} values in the recordings loaded so far.`
  }
  const span = `${formatSessionDate(trend.domain.firstDate)} to ${formatSessionDate(trend.domain.lastDate)}`
  return isComplete
    ? `${span} · your whole history`
    : `${span} · older recordings not loaded yet`
}
